import React from 'react';
import {
    Container,
    Grid,
    useTheme,
    useMediaQuery,
    Typography,
    Box,
    Card,
    CardContent,
    Link,
    Stack,
    IconButton,
    Divider,
} from '@mui/material';
import { FaFacebookMessenger } from 'react-icons/fa';
import { HiChatBubbleBottomCenterText } from 'react-icons/hi2';
import ProfileList from 'components/Profile/Profile'; // Assuming this displays testimonials


const TEXT_COLOR = '#333333';
const HEADING_FONT = '"Poppins", sans-serif';
const BODY_FONT = '"Lato", sans-serif';

const AboutUsPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Container maxWidth="lg" component="main" sx={{ py: 6, fontFamily: BODY_FONT }}>
            <Grid container spacing={5} sx={{ mt: { xs: 8, sm: 10, md: 12 } }}>

                {/* Header Section */}
                <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant={isMobile ? 'h4' : 'h3'}
                            component="h1"
                            gutterBottom
                            sx={{
                                fontWeight: 700,
                                color: "primary.main",
                                fontFamily: HEADING_FONT,
                                textTransform: 'uppercase',
                            }}
                        >
                            Contact Us
                        </Typography>
                        <Typography
                            variant="h6"
                            component="p"
                            color="text.secondary"
                            sx={{ maxWidth: '700px', margin: 'auto', fontFamily: BODY_FONT, lineHeight: 1.6 }}
                        >
                            At HatBazaar, we are passionate about delivering the best local restaurant foods right to your doorstep. We value your experience and are committed to providing top-notch customer service.
                        </Typography>
                    </Box>
                </Grid>

                {/* Testimonials Section */}
                <Grid item xs={12}>
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant={isMobile ? 'h5' : 'h4'}
                            component="h2"
                            gutterBottom
                            sx={{
                                fontWeight: 600,
                                color: "primary.main",
                                fontFamily: HEADING_FONT,
                                mb: 4,
                            }}
                        >
                        </Typography>
                        {/* Assuming ProfileList renders testimonial cards */}
                        <ProfileList />
                    </Box>
                    <Divider sx={{ my: 4 }} />
                </Grid>


                {/* Contact Information Cards Section */}
                <Grid item xs={12}>
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h2"
                        gutterBottom
                        sx={{
                            textAlign: 'center',
                            fontWeight: 600,
                            color: "primary.main",
                            fontFamily: HEADING_FONT,
                            mb: 4,
                        }}
                    >
                        Get In Touch
                    </Typography>
                    <Grid container spacing={4} justifyContent="center">
                        {/* Write to Us Card */}
                        <Grid item xs={12} sm={6} md={4}>
                            <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center', p: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontFamily: HEADING_FONT, fontWeight: 600 }}>
                                        Write to Us
                                    </Typography>
                                    <Link
                                        href="mailto:yourhatbazaar@gmail.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            fontFamily: BODY_FONT,
                                            color: "primary.main",
                                            textDecorationColor: "secondary.main" + '80', // Lighter underline
                                            '&:hover': {
                                                textDecorationColor: "secondary.main",
                                            },
                                            wordBreak: 'break-all' // Prevent overflow
                                        }}
                                    >
                                        yourhatbazaar@gmail.com
                                    </Link>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Office Details Card */}
                        <Grid item xs={12} sm={6} md={4}>
                            <Card elevation={3} sx={{ height: '100%', p: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontFamily: HEADING_FONT, fontWeight: 600, textAlign: 'center' }}>
                                        Office Details
                                    </Typography>
                                    <Box sx={{ fontFamily: BODY_FONT, color: TEXT_COLOR }}>
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Address:</strong> Near old Cinema house, Hariharpara, Murshidabad, WB, 742166
                                        </Typography>
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Contact:</strong> +123 456 789
                                        </Typography>
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Email:</strong> yourhatbazaar@gmail.com
                                        </Typography>
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Hours:</strong> Mon-Sat: 9:30am - 9:30pm | Fri: 3pm-9:30pm
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Chat with Us Card */}
                        <Grid item xs={12} sm={6} md={4}>
                            <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center', p: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontFamily: HEADING_FONT, fontWeight: 600 }}>
                                        Chat with Us
                                    </Typography>
                                    <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                                        <IconButton
                                            href='https://www.facebook.com/yourhatbazaar'
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="Chat on Facebook Messenger"
                                            sx={{ color: "primary.main", '&:hover': { color: "primary.main" } }}
                                        >
                                            <FaFacebookMessenger size={isMobile ? 35 : 40} />
                                        </IconButton>
                                        <IconButton
                                            href="https://tawk.to/hatbazaar"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="Chat via Tawk.to"
                                            sx={{ color: "primary.main", '&:hover': { color: "secondary.main" } }}
                                        >
                                            <HiChatBubbleBottomCenterText size={isMobile ? 35 : 40} />
                                        </IconButton>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Map Section */}
                <Grid item xs={12} sx={{ mt: 6 }}>
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        component="h2"
                        gutterBottom
                        sx={{
                            textAlign: 'center',
                            fontWeight: 600,
                            color: "primary.main",
                            fontFamily: HEADING_FONT,
                            mb: 4,
                        }}
                    >
                        Find Us Here
                    </Typography>
                    <Box sx={{
                        borderRadius: theme.shape.borderRadius,
                        overflow: 'hidden', // Ensures iframe respects border radius
                        border: `1px solid ${theme.palette.divider}`, // Optional border
                        boxShadow: theme.shadows[3],
                    }}>
                        <iframe
                            title="HatBazaar Location"
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3643.6424841209!2d88.4161503754894!3d24.043669377454062!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f961e765f75c53%3A0x83e403c4e0fc4124!2sHatBazaar!5e0!3m2!1sen!2sin!4v1690714458937!5m2!1sen!2sin"
                            style={{
                                width: '100%',
                                height: isMobile ? '350px' : '450px',
                                border: 0,
                                display: 'block' // Removes potential bottom space under iframe
                            }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    </Box>
                </Grid>

            </Grid>
        </Container>
    );
};

export default AboutUsPage;
